import {Card, Icon, Text, WithTooltip} from '@albert-io/atomic';
import {useDroppable} from '@dnd-kit/core';
import {useShallow} from 'zustand/react/shallow';
import {QuestionModelV3} from '@albert-io/models';
import React, {useEffect} from 'react';
import useQuestionStore from 'client/components/QuestionTypes/V2/useQuestionStores';
import {WORD_BANK_ID} from '../store';
import {MovableItem} from '../../shared/MovableItem.react';
import {getSmallSupplement, useIsSmallSupplementsEnabled} from '../../shared/DraggableChip';

interface Props {
  question: QuestionModelV3;
  handleItemClick: (itemId: string) => void;
  handleBankClick?: () => void;
}

const WordBank = ({question, handleItemClick, handleBankClick}: Props) => {
  const useStoreHook = useQuestionStore<'item-bank'>(question);

  const {setNodeRef, node} = useDroppable({id: WORD_BANK_ID});

  const isSmallSupplementsEnabled = useIsSmallSupplementsEnabled();

  const {wordBankItems, activeItemId, isDragging, isSubmitted} = useStoreHook(
    useShallow((state) => ({
      wordBankItems: state.wordBank,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      isSubmitted: state.isSubmitted
    }))
  );

  const handleClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent conflicts with item clicks
    e.stopPropagation();
    handleBankClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleBankClick?.();
    }
  };

  useEffect(() => {
    const handleClickInside = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      const target = e.target as Element;
      const isOnItem = target.closest('[data-item-id]') !== null;
      if (!isOnItem && node.current && node.current.contains(target)) {
        handleBankClick?.();
      }
    };
    document.addEventListener('click', handleClickInside);
    return () => {
      document.removeEventListener('click', handleClickInside);
    };
  }, [handleBankClick, node, activeItemId]);

  return (
    <Card
      ref={setNodeRef}
      className='flex flex-col gap-x1-half border-0 background-bold! p-x1-half'
    >
      <div className='flex items-center justify-between'>
        <Text size='s'>Item Bank:</Text>
        <WithTooltip content='Click or drag an item to move it to the correct answer'>
          <Icon icon='question-circle' color='tertiary' iconStyle='regular' />
        </WithTooltip>
      </div>
      <div className='flex flex-wrap gap-x1-half'>
        {wordBankItems.map((item) => {
          const isInWordBank = item.placedInSlot === null;
          const isSupplement = isSmallSupplementsEnabled && !!getSmallSupplement(item.value);
          return isInWordBank ? (
            <div
              key={item.id}
              className={
                isSupplement
                  ? 'max-w-[calc(50%-var(--spacing-x1-half)/2)] min-w-x15 flex-1 basis-[200px]'
                  : ''
              }
            >
              <MovableItem
                item={item}
                isActive={activeItemId === item.id}
                isDragging={isDragging}
                onClick={() => handleItemClick(item.id)}
                isSubmitted={isSubmitted}
                activeItemId={activeItemId}
              />
            </div>
          ) : (
            <div
              key={item.id}
              className={`p-2 min-h-x4 min-w-x9 rounded background-primary ${isSupplement ? 'max-w-[calc(50%-var(--spacing-x1-half)/2)] min-w-x15 flex-1 basis-[200px]' : ''}`}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              role='button'
              tabIndex={0}
            />
          );
        })}
      </div>
    </Card>
  );
};

export default WordBank;
//
